import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Link } from '@/i18n/navigation';
import { cn } from '@/helpers/utils';

const buttonClasses =
    'gtm-cta-click relative inline-flex justify-center bg-red-800 rounded-[40px] text-white uppercase text-center font-semibold py-[1.45rem] px-10 overflow-hidden group transition-all duration-500 will-change-transform transform';
const glowClasses =
    'absolute bottom-0 left-20 w-80 h-44 bg-gradient-radial from-orange-600 md:from-orange-600/70 to-orange-600/0 rounded-[50%] pointer-events-none group-hover:-translate-x-2/3 group-hover:translate-y-2/3 transition-all ease-in-out duration-500 will-change-transform transform';

type CommonProps = { children: ReactNode; className?: string };
type ButtonAsButton = CommonProps &
    Omit<ComponentPropsWithoutRef<'button'>, 'className'> & { href?: undefined };
type ButtonAsLink = CommonProps &
    Omit<ComponentPropsWithoutRef<'a'>, 'className' | 'href'> & { href: string };

type ButtonProps = ButtonAsButton | ButtonAsLink;

export default function Button({ children, className, ...props }: ButtonProps) {
    const inner = (
        <>
            <span className={glowClasses} />
            <div className="relative">{children}</div>
        </>
    );

    if ('href' in props && props.href !== undefined) {
        const { href, ...rest } = props;
        const isExternal = /^https?:\/\//.test(href);

        if (isExternal) {
            return (
                <a
                    href={href}
                    className={cn(buttonClasses, className)}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...rest}
                >
                    {inner}
                </a>
            );
        }

        return (
            <Link href={href} className={cn(buttonClasses, className)} {...rest}>
                {inner}
            </Link>
        );
    }

    const { disabled, ...rest } = props as ButtonAsButton;
    return (
        <button className={cn(buttonClasses, disabled && 'opacity-50', className)} disabled={disabled} {...rest}>
            {inner}
        </button>
    );
}
